import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetVehicleDistributionReportQuery } from '../../../../api/endpoints/vehicleDistributionReport';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

// Frontend alan adi -> Backend kolon adi
export const backendColumnMap = {
    chassisNo: 'ChassisNo',
    govdeNo: 'GovdeNo',
    dealerName: 'DealerName',
    faturaTarihi: 'FaturaTarihi',
    regionalManager: 'RegionalManager',
    modelCode: 'ModelCode',
    modelName: 'ModelName',
    brand: 'Brand',
    purchasePeriod: 'PurchasePeriod'
};

// Dropdown (kategorik) filtre uygulanan alanlar
export const DROPDOWN_FIELDS = ['dealerName', 'regionalManager', 'modelCode', 'modelName', 'brand', 'purchasePeriod'];

// Metinsel filtre uygulanan alanlar
export const TEXT_FIELDS = ['chassisNo', 'govdeNo'];

// includeFilterOptions ile backend'den deger istenen kolonlar
const FILTER_OPTIONS_COLUMNS = DROPDOWN_FIELDS.map((field) => backendColumnMap[field]);

const OPERATOR_EQUALS = 5;

const INITIAL_FILTER_STATE = {
    chassisNo: null,
    chassisNoMatchMode: null,
    govdeNo: null,
    govdeNoMatchMode: null,
    dealerName: null,
    regionalManager: null,
    modelCode: null,
    modelName: null,
    brand: null,
    purchasePeriod: null
};

const getOperatorValue = (matchMode) => matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || String(value).trim() === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`]),
            value: String(value).trim()
        });
    });

    DROPDOWN_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || value === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: OPERATOR_EQUALS,
            value: String(value)
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = backendColumnMap[field];
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

export default function useVehicleDistributionReport() {
    const initialLazyParams = { first: 0, rows: 10, page: 1 };
    const { getCurrentLocale, getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState(initialLazyParams);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [filterOptions, setFilterOptions] = useState(null);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

    const queryParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            includeFilterOptions: !filterOptionsLoaded,
            filterOptionsColumns: !filterOptionsLoaded ? FILTER_OPTIONS_COLUMNS : null
        };
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, filterOptionsLoaded]);

    const exportParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            languageId: getCurrentLanguageId(),
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            compressionType: 0
        };
    }, [activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    const { data, isFetching, isError, error } = useGetVehicleDistributionReportQuery(queryParams);

    useEffect(() => {
        if (!filterOptionsLoaded && data?.filterOptions) {
            setFilterOptions(data.filterOptions);
            setFilterOptionsLoaded(true);
        }
    }, [data, filterOptionsLoaded]);

    const items = data ? (Array.isArray(data.items) ? data.items : []) : [];
    const totalCount = data ? data.totalCount ?? 0 : 0;

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters((prev) => ({ ...prev, ...updatedFilters }));
        setLazyParams(initialLazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams(initialLazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSortChange = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        const backendColumn = backendColumnMap[nextSortField];

        if (!backendColumn && nextSortOrder !== 0) return;

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams(initialLazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPage = useCallback((e) => {
        setLazyParams({ first: e.first, rows: e.rows, page: e.page + 1 });
    }, []);

    return {
        items,
        totalCount,
        isFetching,
        isError,
        error,
        lazyParams,
        activeFilters,
        filterOptions,
        locale: getCurrentLocale(),
        handleFilterChange,
        resetFilters,
        onPage,
        sortField,
        sortOrder,
        onSort: handleSortChange,
        exportParams
    };
}
